import {
  addErrorHandler,
  getAppStatus,
  LOAD_ERROR,
  pathToActiveWhen,
  registerApplication,
  setUnmountMaxTime,
  SKIP_BECAUSE_BROKEN,
  start,
  unloadApplication,
  type LifeCycles,
} from "single-spa";
import modules from "../../module.config.json";
import { getAccount, handlePromise, refreshAuthToken } from "@illimity/coms-ui/auth";
import {
  fetchEnabledCodes,
  isAdmin,
  requireBaseUrl,
  type ModuleInfo,
} from "@coms/shared";

interface RegisteredModule {
  info: ModuleInfo;
  activeWhen: (location: Location) => boolean;
}

const APP_TITLE = "COMS | Credit Origination & Management System";
const DEFAULT_ERROR_STATUS = 404;
// Unmount budget: same 3000ms single-spa uses by default, but fail-open.
// With dieOnTimeout=false (single-spa default) a Module whose unmount promise
// never settles — e.g. its React tree was already torn down by an uncaught
// render error, so single-spa-react's unmountFinished never fires — blocks the
// reroute forever: no other Module mounts and the page stays blank (error
// #31). With dieOnTimeout=true the hung Module goes to SKIP_BECAUSE_BROKEN,
// the pending navigation completes, and the error handler below ignores it
// because the Module is no longer active. Trade-off: a merely-slow (but
// healthy) unmount over this budget kills the Module until the next reload.
const UNMOUNT_TIMEOUT_MS = 3000;
const ERROR_MESSAGES: Record<number, string> = {
  530: "Modulo non disponibile per verifiche contabili",
  503: "Sistema in manutenzione",
  420: "Modulo temporaneamente non disponibile",
  404: "Modulo non ancora disponibile",
};
const moduleLoadCache = new Map<string, Promise<LifeCycles>>();

const getEnabledModules = async (): Promise<ModuleInfo[]> => {
  const availableModules = (modules as ModuleInfo[]).filter(
    ({ rootModule, code }) => rootModule || code === "header",
  );
  const isUserAdmin = isAdmin(getAccount());
  const groups = isUserAdmin ? ["coms", "admin"] : ["coms"];
  const deps = { baseUrl: requireBaseUrl(), getToken: refreshAuthToken };
  // Fault isolation: one group failing must not block the others. The coms
  // group is required (fail closed without it); admin is best-effort and
  // degrades to coms-only access instead of killing the whole shell.
  const settled = await Promise.allSettled(groups.map((group) => fetchEnabledCodes(group, deps)));
  const codesByGroup = new Map<string, string[]>();
  settled.forEach((result, index) => {
    if (result.status === "fulfilled") {
      codesByGroup.set(groups[index], result.value);
    } else {
      console.error(`Module discovery failed for group "${groups[index]}"`, result.reason);
    }
  });

  const comsCodes = codesByGroup.get("coms");
  if (!comsCodes) {
    throw new Error('Module discovery failed for group "coms"');
  }
  const enabledModuleCodes = new Set([...comsCodes, ...(codesByGroup.get("admin") ?? [])]);

  return availableModules.filter(
    ({ code }) =>
      enabledModuleCodes.has(code) || code === "header" || (isUserAdmin && code === "admin"),
  );
};

const setError = (status: number = DEFAULT_ERROR_STATUS) => {
  const normalizedStatus = ERROR_MESSAGES[status] ? status : DEFAULT_ERROR_STATUS;
  const root = document.getElementById("root");

  window.document.title = `${APP_TITLE} - ${normalizedStatus}`;

  if (!root) {
    console.error("Unable to render the shell error page: #root was not found");
    return;
  }

  root.innerHTML = `
  <div id="notfound">
    <div class="notfound">
      <div>
        <img src="${window.location.origin}/empty.svg" alt="Not Found" />
        <h2>${ERROR_MESSAGES[normalizedStatus]}</h2>
        <p><a href="/">Torna alla home</a></p>
      </div>
    </div>
  </div>
  `;
};

const getAuthenticatedModuleUrl = async (src: string): Promise<string> => {
  const authToken = await refreshAuthToken();
  const moduleUrl = new URL(src, window.location.origin);

  // Native imports cannot send an Authorization header. This is an existing
  // deployment contract and should be replaced with same-origin session auth.
  moduleUrl.searchParams.set("xauth", authToken);
  return moduleUrl.href;
};

const loadModule = (module: ModuleInfo): Promise<LifeCycles> => {
  const cachedModule = moduleLoadCache.get(module.src);
  if (cachedModule) {
    return cachedModule;
  }

  const pendingModule = getAuthenticatedModuleUrl(module.src).then(
    (moduleUrl) => import(/* @vite-ignore */ moduleUrl) as Promise<LifeCycles>,
  );

  moduleLoadCache.set(module.src, pendingModule);
  void pendingModule.catch(() => {
    if (moduleLoadCache.get(module.src) === pendingModule) {
      moduleLoadCache.delete(module.src);
    }
  });

  return pendingModule;
};

const registerModules = (enabledModules: ModuleInfo[]): RegisteredModule[] =>
  enabledModules.map((module) => {
    const activeWhen = pathToActiveWhen(module.path, module.exact);

    registerApplication({
      name: module.name,
      app: () => loadModule(module),
      activeWhen,
    });

    return { info: module, activeWhen };
  });

const getModuleAvailabilityStatus = async (module: ModuleInfo): Promise<number> => {
  try {
    const authToken = await refreshAuthToken();
    const response = await fetch(module.src, {
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    return response.ok ? DEFAULT_ERROR_STATUS : response.status;
  } catch {
    return DEFAULT_ERROR_STATUS;
  }
};

const redactSensitiveUrlParameters = (message: string) =>
  message.replace(/([?&]xauth=)[^&#\s"']+/gi, "$1[REDACTED]");

const installApplicationErrorHandler = (registeredModules: RegisteredModule[]) => {
  const modulesByName = new Map(
    registeredModules.map((registeredModule) => [registeredModule.info.name, registeredModule]),
  );

  addErrorHandler((error) => {
    const registeredModule = modulesByName.get(error.appOrParcelName);
    const appStatus = getAppStatus(error.appOrParcelName);

    console.error("single-spa application failed", {
      name: error.appOrParcelName,
      status: appStatus,
      message: redactSensitiveUrlParameters(error.message),
    });

    if (!registeredModule) {
      setError();
      return;
    }

    const { activeWhen, info: module } = registeredModule;
    if (!activeWhen(window.location)) {
      return;
    }

    if (!module.rootModule) {
      // Chrome (e.g. the header) failed: never wipe the main content for it.
      // Leave its slot empty; single-spa retries the load on the next
      // navigation and failed module URLs are already evicted from the cache.
      console.error(
        `Non-content module "${module.name}" failed with status ${appStatus}; leaving its slot empty`,
      );
      return;
    }

    if (appStatus === LOAD_ERROR) {
      void getModuleAvailabilityStatus(module).then((status) => {
        if (activeWhen(window.location)) {
          setError(status);
        }
      });
      return;
    }

    setError();
  });
};

const syncShellWithLocation = (registeredModules: RegisteredModule[]) => {
  const activeModule = registeredModules.find(
    ({ info, activeWhen }) => info.rootModule && activeWhen(window.location),
  );

  if (!activeModule) {
    setError();
    return;
  }

  if (getAppStatus(activeModule.info.name) === SKIP_BECAUSE_BROKEN) {
    void recoverBrokenModule(activeModule);
    return;
  }

  window.document.title =
    activeModule.info.title && activeModule.info.title !== APP_TITLE
      ? `${APP_TITLE} - ${activeModule.info.title}`
      : APP_TITLE;
};

// Recovery budget: a Module stuck in SKIP_BECAUSE_BROKEN (e.g. after a
// fail-open unmount timeout) never remounts on its own — revisits stay blank
// until a full page reload. Recover once per shell session by unloading it,
// which returns it to NOT_LOADED so the pending reroute re-bootstraps it
// fresh. A repeated failure leaves it broken and shows the error page instead
// of looping recovery forever.
const recoveredBrokenModules = new Set<string>();

const recoverBrokenModule = async ({ info: module, activeWhen }: RegisteredModule): Promise<void> => {
  if (recoveredBrokenModules.has(module.name)) {
    setError();
    return;
  }
  recoveredBrokenModules.add(module.name);
  console.error(`Recovering broken module "${module.name}" via unload`);
  try {
    await unloadApplication(module.name);
  } catch (error) {
    console.error(`Failed to recover module "${module.name}"`, error);
    // Recovery is async: only wipe the page if the broken Module is still
    // the active one. Otherwise the user already moved on to a working
    // Module and must not lose it.
    if (activeWhen(window.location)) {
      setError();
    }
  }
};

const initShell = async () => {
  let enabledModules: ModuleInfo[];
  try {
    enabledModules = await getEnabledModules();
  } catch (error) {
    // Fail closed but stay alive: expose nothing except the header shell, so
    // a grants outage degrades to an error page instead of a dead app with
    // no routing at all. (Errors here carry no tokens, safe to log as-is.)
    console.error("Failed to resolve enabled modules, starting with header only", error);
    enabledModules = (modules as ModuleInfo[]).filter(({ code }) => code === "header");
  }
  const registeredModules = registerModules(enabledModules);

  setUnmountMaxTime(UNMOUNT_TIMEOUT_MS, true);
  installApplicationErrorHandler(registeredModules);
  syncShellWithLocation(registeredModules);
  window.addEventListener("single-spa:routing-event", () => {
    syncShellWithLocation(registeredModules);
  });

  start({ urlRerouteOnly: true });
};

handlePromise(() => {
  void initShell().catch((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    console.error("Failed to initialize the application shell", {
      message: redactSensitiveUrlParameters(message),
    });
    setError();
  });
});
